import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { X, Info, Copy } from "lucide-react";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useAssetNetwork, useSingleBank } from "../hooks/query/queries";
import { useToast } from "../hooks/use-toast";
import { FormattedDate } from "../utils/dateFormatter";
import StringFormatter from "../utils/stringFormatter";
const TransactionHistorySingle = ({
  selectedTransaction,
  openSingle,
  setOpenSingle,
  onClose,
}) => {
  const [LoadingBanks, setLoadingBanks] = useState(false);
  const [isLoadingChain, setIsLoadingChain] = useState(false);
  const { data: networkData } = useAssetNetwork(
    selectedTransaction?.transactionSubtype === "send"
      ? selectedTransaction?.transactionRef?.chain
      : null
  );
  const { toast } = useToast();
  const { data: bank } = useSingleBank(
    selectedTransaction?.transactionRef?.userBankingId
  );

  useEffect(() => {
    if (!bank) {
      setLoadingBanks(true);
    }
    if (bank) {
      setLoadingBanks(false);
    }
    if (!networkData) {
      setIsLoadingChain(true);
    }
    if (networkData) {
      setIsLoadingChain(false);
    }
  }, [bank, networkData]);

  const dateFormat = new FormattedDate();
  const detailedDate = dateFormat.detailedFormat(
    selectedTransaction?.createdAt
  );

  const handleDialogChange = (open) => {
    setOpenSingle(open);
    if (!open) {
      onClose();
    }
  };

  const receiverInfo = selectedTransaction?.transactionRef;
  const receiver =
    receiverInfo?.receiverEmail ??
    receiverInfo?.receiverPayId ??
    receiverInfo?.receiverPhone;

  let receiverTitle = "";

  if (receiverInfo?.receiverEmail) {
    receiverTitle = "Email";
  } else if (receiverInfo?.receiverPayId) {
    receiverTitle = "Pay ID";
  } else if (receiverInfo?.receiverPhone) {
    receiverTitle = "Phone Number";
  }
  const formatter = new CurrencyFormatter();

  const handleCopy = () => {
    const textToCopy =
      selectedTransaction?.transactionHash || selectedTransaction?._id || "N/A";
    navigator.clipboard.writeText(textToCopy);
    toast({
      description: "Copied Successfully",
      variant: "success",
    });
  };
  const parseAmount = (value) => {
    if (value === null || value === undefined || value === "") return 0;

    if (value === 0) return 0;

    if (typeof value === "object" && value !== null) {
      if (value.$numberDecimal) {
        const parsed = parseFloat(value.$numberDecimal);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    }

    if (typeof value === "string") {
      let cleaned = value
        .trim()
        .replace(/^[R$ZARUSD\s]+/i, "")
        .replace(/\s/g, "")
        .replace(/,/g, ".");

      const periodCount = (cleaned.match(/\./g) || []).length;
      if (periodCount > 1) {
        const lastPeriodIndex = cleaned.lastIndexOf(".");
        cleaned =
          cleaned.substring(0, lastPeriodIndex).replace(/\./g, "") +
          cleaned.substring(lastPeriodIndex);
      }

      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }

    if (typeof value === "number") {
      return isNaN(value) ? 0 : value;
    }

    return 0;
  };

  const isFiatCurrency = (asset) => {
    if (!asset) return false;
    const assetUpper = asset.toUpperCase();
    const fiatCurrencies = [
      "ZAR",
      "USD",
      "EUR",
      "GBP",
      "JPY",
      "AUD",
      "CAD",
      "CHF",
      "CNY",
    ];
    return fiatCurrencies.includes(assetUpper);
  };

  const determineSellAssets = (transaction) => {
    const asset = transaction?.asset?.toUpperCase();
    const currency = transaction?.currency?.toUpperCase();

    if (asset && currency) {
      const assetIsFiat = isFiatCurrency(asset);
      const currencyIsFiat = isFiatCurrency(currency);

      if (assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: currency, incomingAsset: asset };
      } else if (!assetIsFiat && currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency };
      } else if (!assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency || "ZAR" };
      } else {
        return {
          outgoingAsset: asset || currency,
          incomingAsset: currency || asset || "ZAR",
        };
      }
    }

    return {
      outgoingAsset: asset || currency || "",
      incomingAsset: currency || asset || "ZAR",
    };
  };

  const isSellTransaction =
    selectedTransaction?.transactionSubtype?.toUpperCase() === "SELL";

  let incomingAsset =
    selectedTransaction?.transactionRef?.incomingAsset?.toUpperCase();
  let outgoingAsset =
    selectedTransaction?.transactionRef?.outgoingAsset?.toUpperCase();

  if (!incomingAsset || !outgoingAsset) {
    if (isSellTransaction) {
      const sellAssets = determineSellAssets(selectedTransaction);
      incomingAsset = incomingAsset || sellAssets.incomingAsset;
      outgoingAsset = outgoingAsset || sellAssets.outgoingAsset;
    } else {
      incomingAsset =
        incomingAsset ||
        selectedTransaction?.asset?.toUpperCase() ||
        selectedTransaction?.currency?.toUpperCase() ||
        null;
      outgoingAsset =
        outgoingAsset ||
        selectedTransaction?.currency?.toUpperCase() ||
        selectedTransaction?.asset?.toUpperCase() ||
        "ZAR";
    }
  }

  const buyCryptoAmount = parseAmount(
    selectedTransaction?.transactionRef?.incomingAmount?.$numberDecimal ||
      selectedTransaction?.transactionRef?.incomingAmount ||
      0
  );

  const buyPrice = parseAmount(
    selectedTransaction?.transactionRef?.price?.$numberDecimal ||
      selectedTransaction?.transactionRef?.price ||
      0
  );

  const buyFiatAmount =
    buyPrice > 0 && buyCryptoAmount > 0
      ? buyCryptoAmount * buyPrice
      : parseAmount(
          selectedTransaction?.transactionRef?.outgoingAmount?.$numberDecimal ||
            selectedTransaction?.transactionRef?.outgoingAmount ||
            selectedTransaction?.transactionRef?.total?.$numberDecimal ||
            selectedTransaction?.transactionRef?.totalAmount?.$numberDecimal ||
            selectedTransaction?.transactionRef?.amount?.$numberDecimal ||
            selectedTransaction?.transactionRef?.amount ||
            0
        );

  const buyFiatAsset = outgoingAsset || "ZAR";

  const totalAmount = buyFiatAmount;
  const totalCostAsset = buyFiatAsset;

  const sellCryptoAmount = parseAmount(
    selectedTransaction?.transactionRef?.outgoingAmount?.$numberDecimal ||
      selectedTransaction?.transactionRef?.outgoingAmount ||
      selectedTransaction?.transactionRef?.total?.$numberDecimal ||
      selectedTransaction?.transactionRef?.totalAmount?.$numberDecimal ||
      selectedTransaction?.amount
  );

  const sellCryptoAsset =
    outgoingAsset ||
    selectedTransaction?.asset?.toUpperCase() ||
    selectedTransaction?.currency?.toUpperCase() ||
    "";

  const sellFiatAmount = parseAmount(
    selectedTransaction?.transactionRef?.incomingAmount?.$numberDecimal ||
      selectedTransaction?.transactionRef?.incomingAmount ||
      selectedTransaction?.transactionRef?.total?.$numberDecimal ||
      selectedTransaction?.transactionRef?.totalAmount?.$numberDecimal ||
      selectedTransaction?.transactionRef?.amount?.$numberDecimal ||
      selectedTransaction?.transactionRef?.amount
  );

  const sellFiatAsset =
    incomingAsset || selectedTransaction?.currency?.toUpperCase() || "ZAR";
  const string = new StringFormatter();
  return (
    <Dialog open={openSingle} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild></DialogTrigger>
      <DialogContent className="max-w-[780px] rounded-2xl p-6 flex-1 flex flex-col">
        <DialogClose asChild>
          <Button
            variant="ghost"
            onClick={() => setOpenSingle(false)}
            className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
          >
            <X size={18} className="text-gray-700" />
          </Button>
        </DialogClose>

        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-800">
            Transaction
          </DialogTitle>
        </DialogHeader>
        {/* BuySell */}
        {selectedTransaction?.transactionType === "UserTrade" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {isSellTransaction
                    ? sellCryptoAsset &&
                      (sellCryptoAmount > 0 || sellCryptoAmount === 0)
                      ? formatter.format(sellCryptoAmount, sellCryptoAsset)
                      : selectedTransaction?.transactionRef?.outgoingAmount
                            ?.$numberDecimal && sellCryptoAsset
                        ? formatter.format(
                            parseAmount(
                              selectedTransaction?.transactionRef
                                ?.outgoingAmount?.$numberDecimal
                            ),
                            sellCryptoAsset
                          )
                        : selectedTransaction?.amount || "N/A"
                    : selectedTransaction?.amount || "N/A"}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionSubtype.toUpperCase()}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">
                  {isSellTransaction ? "Received" : "Approx. value"}
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {isSellTransaction
                    ? sellFiatAmount > 0 || sellFiatAmount === 0
                      ? formatter.format(sellFiatAmount, sellFiatAsset)
                      : selectedTransaction?.transactionRef?.incomingAmount
                            ?.$numberDecimal
                        ? formatter.format(
                            parseAmount(
                              selectedTransaction?.transactionRef
                                ?.incomingAmount?.$numberDecimal
                            ),
                            sellFiatAsset
                          )
                        : "N/A"
                    : buyFiatAmount > 0 || buyFiatAmount === 0
                      ? formatter.format(buyFiatAmount, buyFiatAsset)
                      : selectedTransaction?.transactionRef?.outgoingAmount
                            ?.$numberDecimal
                        ? formatter.format(
                            parseAmount(
                              selectedTransaction?.transactionRef
                                ?.outgoingAmount?.$numberDecimal
                            ),
                            buyFiatAsset
                          )
                        : "N/A"}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Price</span>
                {selectedTransaction?.transactionSubtype.toUpperCase() ===
                "SELL" ? (
                  <span className="text-sm font-bold text-gray-800">
                    {parseAmount(
                      selectedTransaction?.transactionRef?.price
                        ?.$numberDecimal ||
                        selectedTransaction?.transactionRef?.price ||
                        0
                    ).toFixed(6)}{" "}
                    {sellCryptoAsset ||
                      selectedTransaction?.transactionRef?.outgoingAsset?.toUpperCase() ||
                      "N/A"}{" "}
                    /{" "}
                    {sellFiatAsset ||
                      selectedTransaction?.transactionRef?.incomingAsset?.toUpperCase() ||
                      "ZAR"}
                  </span>
                ) : (
                  <span className="text-sm font-bold text-gray-800">
                    {parseAmount(
                      selectedTransaction?.transactionRef?.price
                        ?.$numberDecimal ||
                        selectedTransaction?.transactionRef?.price ||
                        0
                    ).toFixed(6)}{" "}
                    {selectedTransaction?.transactionRef?.incomingAsset?.toUpperCase() ||
                      selectedTransaction?.asset?.toUpperCase()}{" "}
                    /{" "}
                    {selectedTransaction?.transactionRef?.outgoingAsset?.toUpperCase() ||
                      "ZAR"}
                  </span>
                )}
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  Service Charge
                  <Info size={14} className="text-rose-500" />
                </span>

                <span className="text-sm font-bold text-gray-800">
                  {/* {
                    selectedTransaction?.transactionRef?.serviceFee
                      ?.$numberDecimal
                  } */}
                  0
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  {isSellTransaction ? "Total Sold" : "Total Cost"}
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {isSellTransaction
                    ? sellCryptoAsset &&
                      (sellCryptoAmount > 0 || sellCryptoAmount === 0)
                      ? formatter.format(sellCryptoAmount, sellCryptoAsset)
                      : selectedTransaction?.transactionRef?.outgoingAmount
                            ?.$numberDecimal && sellCryptoAsset
                        ? formatter.format(
                            parseAmount(
                              selectedTransaction?.transactionRef
                                ?.outgoingAmount?.$numberDecimal
                            ),
                            sellCryptoAsset
                          )
                        : selectedTransaction?.amount || "N/A"
                    : totalAmount > 0 || totalAmount === 0
                      ? formatter.format(totalAmount, totalCostAsset)
                      : "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* 80EightPay */}
        {selectedTransaction?.transactionType === "Payment" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.amount?.$numberDecimal,
                    selectedTransaction?.asset.toUpperCase()
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionSubtype.toUpperCase()}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">{receiverTitle}</span>
                <span className="text-sm font-bold text-gray-800">
                  {receiver}
                </span>
              </div>

              {selectedTransaction?.transactionSubtype === "send" && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Ref Sender</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.senderReference}
                  </span>
                </div>
              )}
              {selectedTransaction?.transactionSubtype === "receive" && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Ref Receiver</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.anonymous === true
                      ? "Anonymous"
                      : selectedTransaction?.transactionRef?.receiverReference}
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Service Charge</span>
                <span className="text-sm font-bold text-gray-800">
                  {/* {
                    selectedTransaction?.transactionRef?.serviceFee
                      ?.$numberDecimal
                  } */}
                  0
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  Total Cost
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.totalAmount
                      ?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* SendReceive */}
        {selectedTransaction?.transactionType === "CryptoTransaction" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.amount}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionSubtype === "send"
                    ? "Crypto Withdrawal"
                    : "Crypto Deposit"}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              {selectedTransaction?.transactionRef?.notes && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Notes</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.notes}
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Address</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionRef?.walletAddress}
                </span>
              </div>

              {isLoadingChain ? (
                <Skeleton className="flex items-center gap-7 border rounded-2xl  p-2 bg-gray-200" />
              ) : (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Network</span>
                  <span className="text-sm font-bold text-gray-800">
                    {networkData?.chain}
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  {selectedTransaction?.transactionSubtype === "send"
                    ? "Withdrawal Fee"
                    : "Deposit Fee"}
                  <Info size={14} className="text-rose-500" />
                </span>

                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    0,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div>

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  Total Cost
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.totalAmount
                      ?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Withdrawal */}
        {selectedTransaction?.transactionType === "FiatTransaction" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.amount?.$numberDecimal,
                    selectedTransaction?.asset.toUpperCase()
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {/* {selectedTransaction?.transactionSubtype} */}
                  {selectedTransaction.transactionSubtype &&
                    selectedTransaction.transactionSubtype
                      .charAt(0)
                      .toUpperCase() +
                      selectedTransaction.transactionSubtype
                        .slice(1)
                        .toLowerCase()}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              {selectedTransaction?.transactionRef?.notes && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Notes</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.notes}
                  </span>
                </div>
              )}

              {LoadingBanks ? (
                <Skeleton className="flex items-center gap-7 border rounded-2xl  p-2 bg-gray-200" />
              ) : (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Bank</span>
                  <div className="flex flex-col text-right">
                    <span className="font-bold text-sm">{bank?.bankName}</span>
                    <span className="text-xs font-medium text-gray-500">
                      {bank?.accNumber}
                    </span>
                  </div>
                </div>
              )}
              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              {selectedTransaction?.transactionSubtype === "buy" ? (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Price</span>
                  <span className="text-sm font-bold text-gray-800">
                    {parseAmount(
                      selectedTransaction?.transactionRef?.price
                        ?.$numberDecimal ||
                        selectedTransaction?.transactionRef?.price ||
                        0
                    ).toFixed(6)}
                  </span>
                </div>
              ) : (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    Withdrawal Fee
                    <Info size={14} className="text-rose-500" />
                  </span>

                  <span className="text-sm font-bold text-gray-800">
                    {/* {selectedTransaction?.transactionRef?.fees?.$numberDecimal} */}
                    0
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  Total Cost
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.totalAmount
                      ?.$numberDecimal,
                    selectedTransaction?.asset.toUpperCase()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        {selectedTransaction?.transactionType === "Donation" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.amount}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {"Donation"}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              {selectedTransaction?.transactionRef?.notes && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Notes</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.notes}
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Recipient</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionRef?.recipientName}
                </span>
              </div>

              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              {/* <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  {selectedTransaction?.transactionSubtype === "send"
                    ? "Withdrawal Fee"
                    : "Deposit Fee"}
                  <Info size={14} className="text-rose-500" />
                </span>

                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.fee?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div> */}

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  Total Cost
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.amount?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        {selectedTransaction?.transactionType === "Transfer" && (
          <div className="px-6 mt-6">
            <div className="border rounded-lg divide-y">
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Amount</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.amount}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Transaction Type</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.transactionType}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction.status && (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          selectedTransaction.status === "Complete"
                            ? "bg-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          selectedTransaction.status === "Complete"
                            ? "text-green-500"
                            : selectedTransaction.status === "Pending"
                              ? "text-yellow-500"
                              : "text-red-500"
                        }`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  )}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Date</span>
                <div className="flex flex-col text-right">
                  <span className="font-bold text-sm">{detailedDate.date}</span>
                  <span className="text-xs font-medium text-gray-500">
                    {detailedDate.time}
                  </span>
                </div>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Asset</span>
                <span className="text-sm font-bold text-gray-800">
                  {selectedTransaction?.asset?.toUpperCase()}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Activity</span>
                <span className="text-sm font-bold text-gray-800">
                  {string.capitalizeFirst(selectedTransaction?.activity)}
                </span>
              </div>
              {selectedTransaction?.transactionRef?.notes && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Notes</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.notes}
                  </span>
                </div>
              )}

              {selectedTransaction?.transactionRef?.recipientName && (
                <div className="p-4 flex justify-between">
                  <span className="text-sm text-gray-700">Recipient</span>
                  <span className="text-sm font-bold text-gray-800">
                    {selectedTransaction?.transactionRef?.recipientName}
                  </span>
                </div>
              )}

              <div className="p-4 flex justify-between">
                {selectedTransaction?.status === "Pending" ? (
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    TxID
                    <Info size={14} className="text-rose-500" />
                  </span>
                ) : (
                  <span className="text-sm text-gray-700">TxID</span>
                )}
                <span className="flex items-center text-sm gap-3 text-center font-bold text-gray-800">
                  {selectedTransaction?.status === "Pending"
                    ? "N/A"
                    : selectedTransaction?.transactionHash ||
                      selectedTransaction?._id ||
                      "N/A"}
                  {selectedTransaction?.status !== "Pending" &&
                    (selectedTransaction?.transactionHash ||
                      selectedTransaction?._id) && (
                      <Button
                        variant="outline"
                        className="space-x-2 rounded-full w-10 h-10"
                        onClick={handleCopy}
                      >
                        <Copy className="h-5 w-5 text-gray-500 absolute" />
                      </Button>
                    )}
                </span>
              </div>

              {/* <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  {selectedTransaction?.transactionSubtype === "send"
                    ? "Withdrawal Fee"
                    : "Deposit Fee"}
                  <Info size={14} className="text-rose-500" />
                </span>

                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.fee?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div> */}

              <div className="p-4 flex justify-between">
                <span className="text-sm font-bold text-gray-700">
                  Total Cost
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatter.format(
                    selectedTransaction?.transactionRef?.amount?.$numberDecimal,
                    selectedTransaction?.asset?.toUpperCase()
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionHistorySingle;
